import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCategoriesHierarchical } from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { showConfirm, showSuccess } from '../../utils/swal';
import './Header.css';

const Header = () => {
  const { getCartTotals } = useCart();
  const { isAuthenticated, logout } = useAuth();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const cartTotals = getCartTotals();
  const searchRef = useRef(null);

  const handleLogout = async () => {
    const result = await showConfirm('Logout', 'Are you sure you want to logout?', 'Yes, Logout', 'Cancel');
    if (result.isConfirmed) {
      logout();
      showSuccess('Logged Out', 'You have been successfully logged out', 1500).then(() => navigate('/login'));
    }
  };

  useEffect(() => {
    getCategoriesHierarchical()
      .then(data => {
        setCategories((data || []).filter(c => c.isActive).slice(0, 8).map(c => ({
          id: c.id,
          name: c.categoryName,
          link: `/shop?categoryId=${c.id}`,
          subCategories: (c.subCategories || []).filter(s => s.isActive).slice(0, 8).map(s => ({
            id: s.id,
            name: s.subCategoryName || s.name,
            link: `/shop?categoryId=${c.id}&subCategoryId=${s.id}`,
          })),
        })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    if (searchOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchOpen]);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', mobileMenuOpen);
    return () => document.body.classList.remove('no-scroll');
  }, [mobileMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
      setSearchTerm('');
    }
  };

  const closeMenu = () => { setMobileMenuOpen(false); setOpenSubMenu(null); };

  return (
    <>
      {/* ── Search Popup ── */}
      {searchOpen && (
        <div className="search-popup is-visible" onClick={() => setSearchOpen(false)}>
          <div className="search-popup-container" onClick={e => e.stopPropagation()} ref={searchRef}>
            <form role="search" className="search-form" onSubmit={handleSearch}>
              <input type="search" className="search-field" placeholder="Type and press enter"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus />
              <button type="submit" className="search-submit"><i className="icon icon-search"></i></button>
            </form>
            <h5 className="cat-list-title">Browse Categories</h5>
            <ul className="cat-list">
              {categories.map(cat => (
                <li key={cat.id} className="cat-list-item">
                  <Link to={cat.link} onClick={() => setSearchOpen(false)}>{cat.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <header id="header">
        <div id="header-wrap">

          {/* ── Secondary Nav (top bar) ── */}
          <nav className="secondary-nav border-bottom">
            <div className="container">
              <div className="row d-flex align-items-center">
                <div className="col-md-6 col-12 shipping-purchase">
                  <p>Free shipping on a purchase value of $200</p>
                </div>
                <div className="col-md-6 col-12 header-contact text-md-end">
                  <p>Let's talk! <strong>+1 (512) 355-5036</strong></p>
                </div>
              </div>
            </div>
          </nav>

          {/* ── Primary Nav (Menu · Logo · Icons) ── */}
          <nav className="primary-nav padding-small">
            <div className="container">
              <div className="primary-nav-inner">

                {/* Left: hamburger + "Menu" */}
                <div className="pn-left">
                  <button className="menu-toggle-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
                    <span></span><span></span><span></span>
                  </button>
                  <button className="menu-toggle-label" onClick={() => setMobileMenuOpen(true)}>Menu</button>
                </div>

                {/* Center: logo */}
                <div className="pn-center">
                  <Link to="/" className="main-logo">
                    <img src="/assets/imgs/theme/logonew.jpeg" alt="A and U Wholeseller"
                      style={{ height: '52px', width: 'auto', maxWidth: '160px', objectFit: 'contain' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </Link>
                </div>

                {/* Right: search · account · wishlist · cart */}
                <div className="pn-right">
                  <button className="icon-btn pn-icon search-button" onClick={() => setSearchOpen(true)} aria-label="Search">
                    <i className="icon icon-search"></i>
                  </button>
                  <Link to={isAuthenticated ? '/account' : '/login'} className="pn-icon" aria-label="Account">
                    <i className="icon icon-user"></i>
                  </Link>
                  <Link to="/shop-wishlist" className="pn-icon" aria-label="Wishlist">
                    <i className="icon icon-heart"></i>
                    {wishlistCount > 0 && <span className="nav-badge">{wishlistCount}</span>}
                  </Link>
                  <Link to="/shop-cart" className="pn-icon" aria-label="Cart">
                    <i className="icon icon-shopping-cart"></i>
                    {cartTotals.itemCount > 0 && <span className="nav-badge">{cartTotals.itemCount}</span>}
                  </Link>
                </div>

              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* ════════ Slide-out Menu ════════ */}
      {mobileMenuOpen && (
        <>
          <div className="um-overlay" onClick={closeMenu} />
          <div className="um-panel">

            {/* Header row: Menu + × */}
            <div className="um-header">
              <span className="um-title">Menu</span>
              <button className="um-close" onClick={closeMenu}>&#x2715;</button>
            </div>

            {/* Scrollable item list */}
            <ul className="um-list">

              {/* API categories */}
              {categories.map(cat => {
                const subs = cat.subCategories || [];
                const hasSub = subs.length > 0;
                const isOpen = openSubMenu === cat.id;
                return (
                  <li key={cat.id} className="um-item">
                    <div
                      className="um-row"
                      onClick={() => {
                        if (hasSub) setOpenSubMenu(isOpen ? null : cat.id);
                        else { navigate(cat.link); closeMenu(); }
                      }}
                    >
                      <span className="um-label">{cat.name}</span>
                      <span className={`um-arrow${isOpen ? ' um-arrow-open' : ''}`}>&#8250;</span>
                    </div>
                    {hasSub && isOpen && (
                      <ul className="um-sub">
                        {subs.map(sub => (
                          <li key={sub.id}>
                            <Link to={sub.link} onClick={closeMenu} className="um-sublink">{sub.name}</Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}

              {/* Static pages */}
              {[
                { to: '/about',   label: 'About'   },
                { to: '/contact', label: 'Contact'  },
                { to: '/shop',    label: 'Shop'     },
              ].map(item => (
                <li key={item.to} className="um-item">
                  <div className="um-row" onClick={() => { navigate(item.to); closeMenu(); }}>
                    <span className="um-label">{item.label}</span>
                    <span className="um-arrow">&#8250;</span>
                  </div>
                </li>
              ))}

              {/* Account */}
              {(() => {
                const KEY = 'acct';
                const isOpen = openSubMenu === KEY;
                const links = isAuthenticated
                  ? [
                      { to: '/account',          label: 'My Account' },
                      { to: '/account?tab=orders', label: 'My Orders' },
                      { to: '/shop-wishlist',    label: wishlistCount > 0 ? `Wishlist (${wishlistCount})` : 'Wishlist' },
                      { to: '__logout',          label: 'Sign Out' },
                    ]
                  : [
                      { to: '/login',            label: 'Login' },
                      { to: '/register',         label: 'Register' },
                      { to: '/shop-wishlist',    label: 'Wishlist' },
                    ];
                return (
                  <li className="um-item">
                    <div className="um-row" onClick={() => setOpenSubMenu(isOpen ? null : KEY)}>
                      <span className="um-label">{isAuthenticated ? 'My Account' : 'Account'}</span>
                      <span className={`um-arrow${isOpen ? ' um-arrow-open' : ''}`}>&#8250;</span>
                    </div>
                    {isOpen && (
                      <ul className="um-sub">
                        {links.map(l => (
                          <li key={l.label}>
                            {l.to === '__logout'
                              ? <button className="um-sublink um-sublink-btn" onClick={() => { closeMenu(); handleLogout(); }}>{l.label}</button>
                              : <Link to={l.to} onClick={closeMenu} className="um-sublink">{l.label}</Link>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })()}

            </ul>

            {/* Social icons row */}
            <div className="um-social">
              <a href="https://facebook.com"  target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="icon icon-facebook"></i></a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="icon icon-instagram"></i></a>
              <a href="https://twitter.com"   target="_blank" rel="noopener noreferrer" aria-label="Twitter"><i className="icon icon-twitter"></i></a>
              <a href="https://youtube.com"   target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i className="icon icon-youtube-play"></i></a>
            </div>

          </div>
        </>
      )}
    </>
  );
};

export default Header;
